import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ExternalLink,
  KeyRound,
  Package,
  Pencil,
  Plus,
  Search,
  Trash2,
  Zap,
} from "lucide-react";
import { useAppServices } from "../../hook/services";
import { ghl } from "../../enums";
import env from "../../config";
import { buildInstallUrl, cx, formatDate } from "../../utils";
import AppIcon from "../../components/ui/AppIcon";
import Badge from "../../components/ui/Badge";
import Button from "../../components/ui/Button";
import Card from "../../components/ui/Card";
import EmptyState from "../../components/ui/EmptyState";
import Input, { Select } from "../../components/ui/Input";
import Skeleton from "../../components/ui/Skeleton";
import { useConfirm } from "../../components/ui/ConfirmDialog";
import { useToast } from "../../components/ui/Toast";
import Pagination from "../../components/ui/Pagination";
import useListQuery, { useSearchInput } from "../../hook/useListQuery";

const ALL = "All";

// What still needs doing on an app, as filters.
const SETUP = [
  { id: "", label: "Any setup" },
  { id: "keys", label: "Missing client keys", test: (a) => !a.clientId || !a.clientSecret },
  { id: "sso", label: "Missing SSO key", test: (a) => !a.sso },
  { id: "onboarding", label: "No onboarding", test: (a) => !a.getStarted?.video && !a.getStarted?.content },
  { id: "ready", label: "Fully set up", test: (a) => a.clientId && a.clientSecret && a.sso && (a.getStarted?.video || a.getStarted?.content) },
];

// The app list is small and fully loaded for the dashboard anyway, so it's
// filtered and paged here; the address still holds the view.
const DEFAULTS = { page: 1, limit: 20, type: ALL, setup: "", q: "" };

const Apps = () => {
  const { apps } = useAppServices();
  const toast = useToast();
  const confirm = useConfirm();

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useListQuery(DEFAULTS);
  const [search, setSearch] = useSearchInput(query.q, (q) => setQuery({ q }), 150);
  const type = query.type;
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    const { response, error } = await apps.Get();
    if (error) toast(error?.message || "Could not load apps", "error");
    setList(Array.isArray(response) ? response : []);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const types = useMemo(
    () => [ALL, ...Array.from(new Set(list.map((a) => a.appType).filter(Boolean)))],
    [list],
  );

  const matching = useMemo(() => {
    const q = query.q.trim().toLowerCase();
    const setup = SETUP.find((x) => x.id === query.setup)?.test;
    return list.filter((a) => {
      if (type !== ALL && a.appType !== type) return false;
      if (setup && !setup(a)) return false;
      if (!q) return true;
      return [a.appName, a.appId, a.shortDescription, a.clientId]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [list, query.q, query.setup, type]);

  const pages = Math.max(1, Math.ceil(matching.length / query.limit));
  const page = Math.min(query.page, pages);
  const visible = matching.slice((page - 1) * query.limit, page * query.limit);
  const clearFilters = () => setQuery({ q: "", type: ALL, setup: "" });

  const onDelete = async (app) => {
    const ok = await confirm({
      title: `Delete ${app.appName || "this app"}?`,
      message:
        "This permanently removes the app and everything configured on it. Existing installs will stop resolving. This cannot be undone.",
      confirmLabel: "Delete app",
      tone: "danger",
    });
    if (!ok) return;

    setDeletingId(app._id);
    const { error } = await apps.Delete(app._id);
    setDeletingId(null);

    if (error) {
      toast(error?.message || "Could not delete the app", "error");
      return;
    }
    // Drop it locally rather than refetching the whole list.
    setList((prev) => prev.filter((a) => a._id !== app._id));
    toast(`${app.appName || "App"} deleted`);
  };

  return (
    <div className="space-y-5">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[16rem] flex-1">
          <Search
            size={15}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-400"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, app ID or client ID..."
            className="pl-9"
            aria-label="Search apps"
          />
        </div>

        <div className="w-full sm:w-48">
          <Select value={query.setup} onChange={(e) => setQuery({ setup: e.target.value })} aria-label="Filter by setup">
            {SETUP.map((x) => (
              <option key={x.id} value={x.id}>
                {x.label}
              </option>
            ))}
          </Select>
        </div>

        {types.length > 2 && (
          <div className="flex items-center gap-1 rounded-lg border border-ink-200 bg-white p-1 shadow-sm">
            {types.map((t) => (
              <button
                key={t}
                onClick={() => setQuery({ type: t })}
                className={cx(
                  "h-7 rounded-md px-2.5 text-[13px] font-medium transition-colors",
                  type === t ? "bg-ink-900 text-white" : "text-ink-600 hover:bg-ink-100",
                )}
              >
                {t}
              </button>
            ))}
          </div>
        )}

        <Button as={Link} to="/apps/new" icon={<Plus size={14} strokeWidth={2.5} />}>
          New app
        </Button>
      </div>

      {/* Table */}
      <Card padded={false}>
        {loading ? (
          <div className="divide-y divide-ink-150">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-center gap-3 px-5 py-4">
                <Skeleton className="h-9 w-9" rounded="rounded-lg" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-3.5 w-1/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
                <Skeleton className="h-6 w-20" />
              </div>
            ))}
          </div>
        ) : visible.length === 0 ? (
          <EmptyState
            icon={<Package size={24} />}
            title={list.length === 0 ? "No apps yet" : "Nothing matches that search"}
            description={
              list.length === 0
                ? "Create your first marketplace app — you can fill in the details as you go."
                : "Try a different term, or clear the filters."
            }
            action={
              list.length === 0 ? (
                <Button as={Link} to="/apps/new" size="sm" icon={<Plus size={14} />}>
                  New app
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={clearFilters}
                >
                  Clear filters
                </Button>
              )
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[46rem] text-sm">
              <thead>
                <tr className="border-b border-ink-150 text-left">
                  {["App", "Type", "Config", "Created", ""].map((h, i) => (
                    <th
                      key={h || i}
                      className={cx(
                        "px-5 py-3 text-[11px] font-semibold uppercase tracking-wider text-ink-500",
                        i === 4 && "text-right",
                      )}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-150">
                {visible.map((app) => (
                  <tr key={app._id} className="group transition-colors hover:bg-ink-50">
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <AppIcon app={app} size="sm" />
                        <div className="min-w-0">
                          <Link
                            to={`/apps/${app._id}/edit`}
                            className="block truncate font-medium text-ink-900 hover:text-brand-600"
                          >
                            {app.appName || "Untitled app"}
                          </Link>
                          <span className="block truncate font-mono text-[11px] text-ink-400">
                            {app.appId || "no app id"}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      {app.appType ? (
                        <Badge tone={app.appType === ghl.AppTypes.AGENCY ? "violet" : "neutral"}>
                          {app.appType}
                        </Badge>
                      ) : (
                        <span className="text-ink-400">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex flex-wrap items-center gap-1.5">
                        {app.formFields?.length > 0 && (
                          <Badge tone="emerald" icon={<KeyRound size={10} />}>
                            {app.formFields.length}
                          </Badge>
                        )}
                        {(app.actions?.length > 0 || app.triggers?.length > 0) && (
                          <Badge tone="amber" icon={<Zap size={10} />}>
                            {(app.actions?.length || 0) + (app.triggers?.length || 0)}
                          </Badge>
                        )}
                        {!app.getStarted?.video && !app.getStarted?.content && (
                          <Badge tone="rose">No onboarding</Badge>
                        )}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-5 py-3.5 text-ink-500">
                      {formatDate(app.createdAt)}
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-end gap-1">
                        {app.appId && (
                          <a
                            href={buildInstallUrl(env.GHL_MARKETPLACE_URL, app.appId)}
                            target="_blank"
                            rel="noreferrer"
                            title="View in marketplace"
                            className="grid h-8 w-8 place-items-center rounded-lg text-ink-400 hover:bg-ink-200/60 hover:text-ink-700"
                          >
                            <ExternalLink size={14} />
                          </a>
                        )}
                        <Link
                          to={`/apps/${app._id}/edit`}
                          title="Edit"
                          className="grid h-8 w-8 place-items-center rounded-lg text-ink-400 hover:bg-ink-200/60 hover:text-ink-700"
                        >
                          <Pencil size={14} />
                        </Link>
                        <button
                          onClick={() => onDelete(app)}
                          disabled={deletingId === app._id}
                          title="Delete"
                          className="grid h-8 w-8 place-items-center rounded-lg text-ink-400 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-40"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && (
          <Pagination
            page={page}
            pages={pages}
            total={matching.length}
            limit={query.limit}
            noun={matching.length === 1 ? "app" : "apps"}
            onPage={(p) => setQuery({ page: p })}
            onLimit={(limit) => setQuery({ limit })}
          />
        )}
      </Card>
    </div>
  );
};

export default Apps;
